import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CommandPaletteContextValue } from '@/types/keyboard-shortcuts';
import { useKeyboardShortcuts } from './KeyboardShortcutsContext';

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const { registerShortcut } = useKeyboardShortcuts();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    const unregister = registerShortcut({
      id: 'command-palette-toggle',
      keys: { key: 'k', modifiers: ['ctrl'] },
      label: 'Open Command Palette',
      description: 'Search for commands and shortcuts',
      action: toggle,
      category: 'actions',
      showInPalette: false,
    });
    return unregister;
  }, [registerShortcut, toggle]);

  return (
    <CommandPaletteContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette() {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used within CommandPaletteProvider');
  }
  return context;
}
